import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useLocation, useRouteError } from 'react-router';
import { Lock } from 'lucide-react';
import { BRAND } from '@shripos/shared/brand';
import { Alert, Button, PageLoader } from '@shripos/ui';
import { api, handleAuthError, logout } from '../lib/api.js';
import { useHeartbeat } from '../lib/realtime.js';
import { useSession } from '../stores/session.js';
import { useOffline, isNetworkError } from '../stores/offline.js';
import { kvGet } from '../lib/offline/db.js';
import { markOfflineSession, wasOfflineSession } from '../lib/offline/staff.js';

/** On start-up: is this a registered device, and is someone signed in? */
export function AppBoot() {
  const status = useSession((s) => s.status);
  useHeartbeat();
  useEffect(() => {
    if (status !== 'loading') return;
    (async () => {
      let offline = false;
      const device = await api.get('/devices/current').catch(async (err) => {
        if (!isNetworkError(err)) return null;
        offline = true;
        return kvGet('device');
      });
      useSession.getState().setDevice(device);
      try {
        const me = await api.get('/auth/me');
        // Someone switched in offline and the app restarted: the server session may be another
        // person's, so ask for a PIN instead of carrying on as them.
        if (await wasOfflineSession()) {
          const last = await kvGet('me');
          await markOfflineSession(false);
          if (last?.user?.id !== me.user.id) {
            await api.post('/auth/logout').catch(() => {});
            useSession.getState().signedOut('offlineEnded');
            return;
          }
        }
        useSession.getState().signedIn(me);
      } catch (err) {
        if (err.code === 'LICENSE_SUSPENDED' || err.code === 'LICENSE_EXPIRED') handleAuthError(err);
        else if ((offline || isNetworkError(err)) && device) {
          // No internet: a registered device carries on with the last signed-in profile.
          const me = await kvGet('me');
          useOffline.getState().set({ reachable: false });
          if (me?.device?.id && me.device.id === device.device?.id) {
            if (await wasOfflineSession()) useSession.getState().signedInOffline(me);
            else useSession.getState().signedIn(me);
          } else useSession.getState().signedOut();
        } else useSession.getState().signedOut();
      }
    })();
  }, [status]);
  if (status === 'loading') return <PageLoader label={`Starting ${BRAND.productName}…`} />;
  if (status === 'locked') return <LockedScreen />;
  return <Outlet />;
}

/**
 * Signed-in routes. Enforces, in order: forced password change, outlet selection.
 * `bare` routes (change password, choose outlet) skip those redirects.
 */
export function RequireAuth({ children, bare = false }) {
  const status = useSession((s) => s.status);
  const me = useSession((s) => s.me);
  const device = useSession((s) => s.device);
  const location = useLocation();
  if (status !== 'authenticated') {
    // A tablet registered as a self-order kiosk opens straight into ordering mode.
    if (device?.device?.kind === 'self_order') return <Navigate to="/self-order" replace />;
    return <Navigate to={device ? '/pin' : '/login'} replace state={{ from: location.pathname }} />;
  }
  if (!bare) {
    if (me.user.mustChangePassword) return <Navigate to="/change-password" replace />;
    if (!me.outlet && me.outlets.length > 1 && !me.device) return <Navigate to="/select-outlet" replace />;
  }
  return children ?? <Outlet />;
}

export function RequireAnonymous() {
  const status = useSession((s) => s.status);
  if (status === 'authenticated') return <Navigate to="/" replace />;
  return <Outlet />;
}

export function LockedScreen() {
  const { t } = useTranslation();
  const lock = useSession((s) => s.lock);
  return (
    <div className="flex min-h-full flex-col items-center justify-center gap-4 bg-slate-900 p-6 text-center text-white">
      <div className="rounded-full bg-white/10 p-4">
        <Lock className="size-10" />
      </div>
      <h1 className="text-2xl font-semibold">{t('license.suspendedTitle')}</h1>
      <p className="max-w-md text-slate-300">{t('license.suspended')}</p>
      {lock?.reason && <p className="max-w-md text-slate-400">{t('license.reason', { reason: lock.reason })}</p>}
      <Button
        variant="secondary"
        onClick={async () => {
          await logout();
          window.location.assign('/login');
        }}
      >
        {t('common.signOut')}
      </Button>
    </div>
  );
}

export function RouteError() {
  const error = useRouteError();
  return (
    <div className="mx-auto max-w-lg p-8">
      <Alert tone="danger" title="Something went wrong">
        {error?.message ?? 'Unexpected error'}
      </Alert>
      <Button className="mt-4" variant="secondary" onClick={() => window.location.assign('/')}>
        Reload
      </Button>
    </div>
  );
}
